import {
    PotentialValue,
    ActionResult,
    ActionSource,
    RenderEnvironment,
    Context,
    asMaybeAction,
    Action,
    ForceValueProducer,
} from "common/components/types";
import { useCallback, useMemo, useRef, useState } from "react";
import { useForce } from "hooks/use-force";
import { useEditComponentEnabledViaNetworkStatus } from "hooks/use-edit-components-enabled-via-network-status";

export type OnClickHandler = ((handled: boolean | undefined) => Promise<ActionResult | undefined>) | undefined;
export type OnClickHandlerWithCustomContext =
    | ((customCtx: Context, handled: boolean | undefined) => Promise<ActionResult | undefined>)
    | undefined;

// returns [isEnabled, actionShouldBeVisible]
export function getIsActionEnabled(
    ctx: Context,
    env: RenderEnvironment,
    force: ForceValueProducer,
    editingAllowed: boolean,
    onLine: boolean,
    action: PotentialValue
): [boolean, boolean] {
    if (!(action instanceof Action) || action.isNoAction) return [false, false];
    if (!action.canExecute(ctx, force)) return [false, false];
    if (onLine) return [true, true];

    const [mutatesData, needsOnline] = action.needsOnline(ctx, force, env);
    if (needsOnline) {
        return [false, true];
    } else if (mutatesData) {
        return [editingAllowed, true];
    } else {
        return [true, true];
    }
}

export function useOnClickFromAction(
    onTap: PotentialValue,
    source: ActionSource,
    ctx: Context,
    env: RenderEnvironment
): {
    onClick: OnClickHandler;
    targetURL: string | undefined;
    autoRunInTabletMode: boolean;
    busy: boolean;
    onClickWithCustomContext: OnClickHandlerWithCustomContext;
    actionIsVisible: boolean;
} {
    const action = useMemo(() => asMaybeAction(onTap), [onTap]);
    const [busy, setBusy] = useState(false);
    const [force] = useForce();

    const actionRef = useRef(action);
    const ctxRef = useRef(ctx);
    const envRef = useRef(env);
    const busyRef = useRef(busy);

    // Refs are used to avoid re-creating the callback on every change.
    actionRef.current = action;
    ctxRef.current = ctx;
    envRef.current = env;
    busyRef.current = busy;

    const [editingAllowed, onLine] = useEditComponentEnabledViaNetworkStatus(env);
    const [isEnabled, actionShouldBeVisible] = getIsActionEnabled(ctx, env, force, editingAllowed, onLine, action);

    const onClick: OnClickHandler = useCallback(
        async (handled: boolean | undefined) => {
            const a = actionRef.current;
            if (a === undefined || busyRef.current) return Promise.resolve(undefined);
            const fn = async () => {
                setBusy(true);
                const result = await a.run(ctxRef.current, envRef.current, source, handled);
                setBusy(false);
                return result;
            };
            return fn();
        },
        [source]
    );
    const targetURL = action?.targetURL(ctx, env, source);
    const onClickWithCustomContext: OnClickHandlerWithCustomContext = useCallback(
        async (customCtx: Context, handled: boolean | undefined) => {
            const a = actionRef.current;
            if (a === undefined || busyRef.current) return Promise.resolve(undefined);
            const fn = async () => {
                setBusy(true);
                const result = await a.run(customCtx, envRef.current, source, handled);
                setBusy(false);
                return result;
            };
            return fn();
        },
        [source]
    );

    return {
        onClick: isEnabled ? onClick : undefined,
        targetURL,
        autoRunInTabletMode: (!env.appEnvironment.isBuilder && action?.autoRunInTabletMode) === true,
        busy,
        onClickWithCustomContext: isEnabled ? onClickWithCustomContext : undefined,
        actionIsVisible: actionShouldBeVisible,
    };
}
